import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import WhatsAppButton from "../Common/WhatsAppButton";
import LogoLoader from "../Common/LogoLoader";

const PROMPTS = [
  "How do I sell my materials?",
  "Where is my payment?",
  "What is in my bin?",
  "What are today's prices?",
];

function MessageText({ text, light }) {
  const nodes = [];
  const pattern = /\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]+)\)/g;
  let last = 0;
  let match = pattern.exec(text || "");
  let key = 0;
  while (match) {
    if (match.index > last) nodes.push((text || "").slice(last, match.index));
    const href = match[2];
    let path = href;
    try {
      const url = new URL(href, "https://jhonks.com");
      if (url.hostname.endsWith("jhonks.com")) path = `${url.pathname}${url.search}`;
    } catch (error) {
      path = href;
    }
    const className = light ? "font-semibold underline" : "font-semibold text-green-800 underline";
    nodes.push(
      path.startsWith("/") ? (
        <Link key={key} to={path} className={className}>{match[1]}</Link>
      ) : (
        <a key={key} href={href} className={className} target="_blank" rel="noreferrer">{match[1]}</a>
      )
    );
    key += 1;
    last = match.index + match[0].length;
    match = pattern.exec(text || "");
  }
  if (last < (text || "").length) nodes.push((text || "").slice(last));
  return nodes;
}

const SupportPage = () => {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    const response = await api.get("/support/thread");
    setThread(response.data.data.thread);
    setError("");
  };

  useEffect(() => {
    load()
      .catch(() => setError("Support is unavailable right now. Try again in a moment."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread, sending]);

  const sendText = async (value) => {
    const body = String(value || "").trim();
    if (!body || sending) return;
    setSending(true);
    setError("");
    setText("");
    setThread((current) => ({
      ...(current || { messages: [], mode: "ai" }),
      messages: [...(current?.messages || []), { from: "user", text: body, at: Date.now() }],
    }));
    try {
      const response = await api.post("/support/messages", { text: body });
      setThread(response.data.data.thread);
    } catch (sendError) {
      setError(sendError.response?.data?.message || "The message did not send. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    setSending(true);
    try {
      const response = await api.post("/support/escalate");
      setThread(response.data.data.thread);
    } catch (escalateError) {
      setError("Could not reach a person yet. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const clearChat = async () => {
    if (sending || !(thread?.messages || []).length) return;
    setSending(true);
    setError("");
    try {
      const response = await api.post("/support/clear");
      setThread(response.data.data.thread);
      setText("");
      setConfirmClear(false);
    } catch (clearError) {
      setConfirmClear(false);
      setError(clearError.response?.data?.message || "The chat could not be cleared.");
    } finally {
      setSending(false);
    }
  };

  const human = thread?.mode === "human";

  return (
    <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[280px_1fr]">
      <aside className="space-y-4">
        <div className="rounded-3xl bg-gradient-to-br from-green-700 to-green-900 p-6 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-green-100">Jhonks care</p>
          <h1 className="mt-2 text-2xl font-bold">We are here to help you sell</h1>
          <p className="mt-2 text-sm text-green-50">
            The assistant knows the Jhonks flow. A person can take over the same conversation.
          </p>
        </div>
        <div className="rounded-3xl bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-900">Common questions</p>
          <div className="mt-3 flex flex-col gap-2">
            {PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => sendText(prompt)}
                className="rounded-2xl bg-gray-50 px-3 py-3 text-left text-sm font-medium text-gray-800 hover:bg-green-50"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-3xl bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-900">Prefer WhatsApp?</p>
          <p className="mt-1 text-sm text-gray-500">Request a collection without leaving the chat.</p>
          <div className="mt-3">
            <WhatsAppButton />
          </div>
        </div>
      </aside>

      <section className="flex min-h-[70vh] flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">{human ? "Jhonks support" : "Jhonks assistant"}</p>
            <p className="text-xs text-gray-500">
              {human ? "A person has this conversation." : "Usually replies in a few seconds."}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              disabled={sending || !(thread?.messages || []).length}
              className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
            >
              New enquiry
            </button>
            <button
              type="button"
              onClick={escalate}
              disabled={human || sending}
              className="rounded-full border border-green-700 px-4 py-2 text-sm font-semibold text-green-800 disabled:opacity-50"
            >
              {human ? "A person is here" : "Talk to a person"}
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto bg-gray-50 px-5 py-6">
          {loading && <LogoLoader label="Opening support" />}
          {!loading && !(thread?.messages || []).length && (
            <div className="mx-auto max-w-md rounded-3xl bg-white px-6 py-8 text-center shadow-sm">
              <p className="text-lg font-bold text-gray-900">How can we help today?</p>
              <p className="mt-2 text-sm text-gray-500">
                Ask about your bin, an agent, a payment, or how to sell. Links in a reply open the right page.
              </p>
            </div>
          )}
          {(thread?.messages || []).map((message, index) => {
            const mine = message.from === "user";
            return (
              <div key={`${message.at || index}-${index}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-3xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                    mine ? "rounded-br-md bg-green-700 text-white" : "rounded-bl-md bg-white text-gray-800"
                  }`}
                >
                  <p className={`mb-1 text-xs font-semibold ${mine ? "text-green-100" : "text-green-800"}`}>
                    {mine ? "You" : message.from === "human" ? "Jhonks support" : "Jhonks assistant"}
                  </p>
                  <MessageText text={message.text} light={mine} />
                </div>
              </div>
            );
          })}
          {sending && (
            <div className="flex justify-start">
              <div className="rounded-3xl rounded-bl-md bg-white px-4 py-3 text-sm text-gray-500 shadow-sm">
                Writing a reply…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {error && <p className="bg-red-50 px-5 py-2 text-sm text-red-700">{error}</p>}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            sendText(text);
          }}
          className="flex gap-2 border-t border-gray-100 bg-white p-4"
        >
          <input
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Write a message"
            className="flex-1 rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-green-700"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="rounded-2xl bg-green-700 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </section>

      {confirmClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4" onClick={() => !sending && setConfirmClear(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="clear-chat-title"
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <p id="clear-chat-title" className="text-lg font-bold text-gray-900">Start a new enquiry?</p>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              This chat will be cleared. Your next message starts a fresh conversation with the assistant.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setConfirmClear(false)}
                disabled={sending}
                className="rounded-2xl border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 disabled:opacity-50"
              >
                Keep this chat
              </button>
              <button
                type="button"
                onClick={clearChat}
                disabled={sending}
                className="rounded-2xl bg-green-700 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {sending ? "Clearing…" : "Clear and start again"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
